import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus("error");
      setMessage("Invalid verification link");
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
      setStatus("success");
      setMessage(response.data.message);
      toast.success("Email verified successfully!");
    } catch (error) {
      setStatus("error");
      setMessage(error.response?.data?.detail || "Verification failed");
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-slate-900 relative"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1705417272217-490f4511abeb?w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
      
      <Card className="w-full max-w-md mx-4 relative z-10 border-slate-200 shadow-2xl" data-testid="verify-email-card">
        <CardContent className="pt-8 text-center">
          {status === "loading" && (
            <>
              <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-slate-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Verifying...</h2>
              <p className="text-slate-500">Please wait while we verify your email.</p>
            </>
          )}
          
          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Email Verified!</h2>
              <p className="text-slate-500 mb-6">{message}</p>
              <Button 
                className="w-full"
                onClick={() => navigate("/login")}
                data-testid="go-to-login-button"
              >
                Sign In to Your Account
              </Button>
            </>
          )}
          
          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Verification Failed</h2>
              <p className="text-slate-500 mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/login">
                  <Button className="w-full">Go to Sign In</Button>
                </Link>
                <p className="text-sm text-slate-400">
                  Need a new verification link?{" "}
                  <Link to="/register" className="text-orange-600 hover:text-orange-700">
                    Register again
                  </Link>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
